import Decimal from "decimal.js"
import React, { useContext, useMemo } from "react"
import { useMarina } from "src/contexts/MarinaContext"
import { centsToDollars, dollarsToCents } from "src/main/Billing/Items/helpers"
import { CONVENIENCE_FEE_FUNDING_METHOD } from "src/main/Billing/constants"
import { POSContext } from "src/main/PointOfSale/Layout"

import LineItems from "src/components/LineItems/LineItems"

import { PaymentFlowContext } from "../PaymentModal"
import { CheckoutContext } from "../index"
import TipInput from "./TipInput"

const ConfirmationTotals = () => {
  const { presentmentCurrency } = useMarina()
  const {
    cartController: { totals, lineItems, expectedSurcharge },
  } = useContext(POSContext)
  const {
    checkoutDetails: { tip, setTip },
    flowDetails: {
      allowTipsForPaymentFlow,
      surchargeEligible,
      optimisticSurchargeDisplay,
      selectedCardFunding,
    },
  } = useContext(PaymentFlowContext)
  const { sale: retrySale } = useContext(CheckoutContext)

  // The reader can't know the card's funding until capture, so it shows the
  // surcharge optimistically (and hedges the label). The other surcharge-eligible
  // flows know the selected card's funding at confirmation and show the line only
  // when it's a credit card — debit/prepaid/unknown get no surcharge.
  const fundingAllowsSurcharge =
    optimisticSurchargeDisplay ||
    selectedCardFunding === CONVENIENCE_FEE_FUNDING_METHOD

  // Added to the total whenever shown, including on retry. Unlike the tip (a
  // ProductSale that persists in retrySale.total), the surcharge is returned on
  // a failed payment and re-assessed on the retry, so it's never already baked
  // into retrySale.total.
  const showSurcharge =
    surchargeEligible && fundingAllowsSurcharge && expectedSurcharge > 0

  const tipAmount = useMemo(() => {
    if (retrySale?.tip) {
      return centsToDollars(retrySale.tip).toFixed(2).toString()
    } else if (allowTipsForPaymentFlow) {
      return new Decimal(tip).toFixed(2).toString()
    } else {
      return "0.00"
    }
  }, [retrySale, allowTipsForPaymentFlow, tip])

  const totalAmount = useMemo(() => {
    // retrySale.total already includes any persisted tip; a fresh sale sums the
    // cart total + tip. The surcharge is additive in both cases (see showSurcharge).
    const baseTotal = retrySale
      ? centsToDollars(retrySale.total)
      : Decimal.sum(totals.total, tipAmount)

    const withSurcharge = showSurcharge
      ? Decimal.sum(baseTotal, centsToDollars(expectedSurcharge))
      : new Decimal(baseTotal)

    return withSurcharge.toFixed(2).toString()
  }, [retrySale, tipAmount, totals.total, showSurcharge, expectedSurcharge])

  const showTipLine = useMemo(() => {
    if (retrySale) {
      return retrySale.tip > 0
    }

    return allowTipsForPaymentFlow
  }, [retrySale, allowTipsForPaymentFlow])

  const lineItemsWithTip = useMemo(() => {
    // Backend lineItems includes subtotal, discount, taxes, and total
    // We keep the total but add tip line before it (if present)
    const items = lineItems.filter((item) => item.type !== "total")

    // Check if backend already included a tip line (happens after sale creation)
    const backendHasTip = items.some((item) => item.label === "Tip")

    // Add tip line if needed (only if not retrying sale and backend hasn't already included it)
    if (showTipLine && !retrySale && !backendHasTip) {
      items.push({
        type: "subtotal",
        label: "Tip",
        amount: dollarsToCents(tipAmount),
      })
    }

    if (showSurcharge) {
      items.push({
        type: "subtotal",
        label: optimisticSurchargeDisplay
          ? "Convenience Fee (if paying with credit)"
          : "Convenience Fee",
        amount: expectedSurcharge,
      })
    }

    // Add total line with recalculated amount (includes tip + surcharge)
    items.push({
      type: "total",
      label: "Total",
      amount: dollarsToCents(totalAmount),
    })

    return items
  }, [
    lineItems,
    showTipLine,
    retrySale,
    tipAmount,
    totalAmount,
    showSurcharge,
    optimisticSurchargeDisplay,
    expectedSurcharge,
  ])

  return (
    <div className="space-y-4 py-4">
      <LineItems
        lineItems={lineItemsWithTip}
        loading={false}
        currency={presentmentCurrency}
        spacing={2}
      />
      <hr />
      {!retrySale && allowTipsForPaymentFlow ? (
        <TipInput tip={tip} setTip={setTip} />
      ) : null}
    </div>
  )
}

export default ConfirmationTotals
